import React, { useState } from 'react';
import { Container, Typography, Button, Box, TextField, Alert, Paper } from '@mui/material';
import { emailService } from '../services/emailService';

const EmailTestPage: React.FC = () => {
  const [testResult, setTestResult] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');

  const sendTestEmail = async () => {
    setLoading(true);
    setTestResult('');

    try {
      // Test the email service directly instead of going through webhook
      const testData = {
        orderNumber: 'TEST-' + Math.floor(Math.random() * 10000),
        customerName: name || 'Test Customer',
        customerEmail: email || 'test@example.com',
        productName: 'Professionally Installed HAVEN Central Air Bundle'
      };

      console.log('Sending test email with data:', testData);
      
      const success = await emailService.sendPostPurchaseQuestionnaire(testData);
      
      if (success) {
        setTestResult(`✅ SUCCESS!\n\nEmail sent successfully to: ${testData.customerEmail}\nOrder Number: ${testData.orderNumber}\n\nCheck your email inbox for the professional installation questionnaire.`);
      } else {
        setTestResult(`❌ FAILED!\n\nEmail could not be sent. This could be due to:\n- Invalid email address\n- Mandrill API issues\n- Network connectivity\n\nCheck the browser console for more details.`);
      }
    } catch (error) {
      console.error('Email test error:', error);
      setTestResult(`❌ ERROR!\n\n${error}\n\nCheck the browser console for full error details.`);
    }

    setLoading(false);
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom align="center">
        Email System Test
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Test Professional Installation Email
        </Typography>
        
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            label="Test Email Address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your-email@example.com"
            sx={{ mb: 2 }}
            required
          />
          
          <TextField
            fullWidth
            label="Test Customer Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="John Doe"
            sx={{ mb: 2 }}
          />
        </Box>

        <Button 
          variant="contained" 
          onClick={sendTestEmail} 
          disabled={loading || !email.trim()}
          sx={{ mb: 2 }}
        >
          {loading ? 'Sending Test Email...' : 'Send Test Email'}
        </Button>

        {testResult && (
          <Alert severity={testResult.includes('✅') ? 'success' : 'error'} sx={{ mt: 2 }}>
            <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '12px' }}>
              {testResult}
            </pre>
          </Alert>
        )}
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Testing Instructions
        </Typography>
        <Typography variant="body2" color="text.secondary">
          1. Enter your real email address above<br/>
          2. Click "Send Test Email" to test the email service directly<br/>
          3. Check your email for the professional installation questionnaire<br/>
          4. If successful, the email system is working correctly<br/>
          5. The webhook will work automatically on the live site when orders are placed
        </Typography>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          How This Works in Production
        </Typography>
        <Typography variant="body2" color="text.secondary">
          <strong>🔄 Complete Flow:</strong><br/>
          1. Customer purchases Professional Installation on your store<br/>
          2. Shopify webhook fires → calls your Netlify function<br/>
          3. Function automatically sends this same questionnaire email<br/>
          4. Customer receives email and clicks the questionnaire link<br/>
          5. You receive their responses to plan the installation<br/><br/>
          
          <strong>📧 What you're testing:</strong><br/>
          This test bypasses the webhook and directly tests the email service to verify your Mandrill setup is working correctly.
        </Typography>
      </Paper>
    </Container>
  );
};

export default EmailTestPage; 